import { AbstractControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import * as lodash from 'lodash';

export function findInvalidControlNames(control: FormGroup): string[] {
  const results: string[] = [];
  if (control.disabled) return results;

  const controls = control.controls;
  for (const key of Object.keys(controls)) {
    const child = controls[key];
    // skip disabled controls entirely
    if (child.disabled) continue;

    if (isFormGroup(child)) {
      const nested = findInvalidControlNames(child);
      if (nested.length) {
        nested.forEach((n) => results.push(`${key}.${n}`));
      } else if (child.invalid) {
        results.push(key);
      }
    } else {
      if (child.invalid) results.push(key);
    }
  }
  return results;
}

function isFormGroup(ctrl: AbstractControl): ctrl is FormGroup | UntypedFormGroup {
  return ctrl instanceof UntypedFormGroup || ctrl instanceof FormGroup;
}

// find first selectable control path (e.g. 'a' or 'outer.inner')
function findFirstControlPath(formGroup: FormGroup): string | null {
  const controls = formGroup.controls;
  for (const key of Object.keys(controls)) {
    const child = controls[key];
    if (child.disabled) continue;
    // find next available control if the control turns out to be a formGroup
    if (isFormGroup(child)) {
      continue;
    }
    return key;
  }
  return null;
}

export function scrollToFirstMatchingSelector(
  root: ParentNode | null | undefined,
  selectors: string[],
): boolean {
  if (!root) return false;
  const rootEl = root as Element;
  try {
    for (const selector of selectors) {
      const found = rootEl.querySelector(selector) as HTMLElement | null;
      if (!found || hasDisplayNoneInHierarchy(found)) continue;

      found.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        found.focus();
      } catch {
        // skip
      }
      return true;
    }
    return false;
  } catch (err) {
    return false;
  }
}

export function getSelectorsForControlName(name: string): string[] {
  const kebab = lodash.kebabCase(name);
  const lower = name.toLowerCase();
  return [
    `[formControlName="${name}"]`,
    `[formControlName="${kebab}"]`,
    `[formControlName="${lower}"]`,
    `[name="${name}"]`,
    `[name="${kebab}"]`,
    `[name="${lower}"]`,
    `#${name}`,
    `#${kebab}`,
    `#${lower}`,
    `[data-testid="${name}"]`,
    `[data-testid="${kebab}"]`,
    `[data-testid="${lower}"]`,
  ];
}

export function scrollToFirstInvalidControl(
  groups: (FormGroup | null | undefined)[],
  root: ParentNode = document,
): void {
  const validGroups = (groups ?? []).filter(Boolean) as FormGroup[];
  if (!Array.isArray(validGroups) || !validGroups.length) return;

  const invalidNames: string[] = [];
  for (const group of validGroups) {
    try {
      let invalidControlNames = findInvalidControlNames(group);

      /* If group-level validator made the group invalid but no child controls are invalid,
          pick the first child control path so we can target an element to scroll to.
          this can happen if a group of checkboxes requires at least one checkbox
          to be selected but none of the checkboxes are mandatory on their own
        */
      if (!invalidControlNames.length && group.invalid) {
        const firstPath = findFirstControlPath(group);
        if (firstPath) invalidControlNames = [firstPath];
      }

      for (const path of invalidControlNames) {
        const segments = path.split('.');
        const lastName = segments[segments.length - 1];
        if (lastName && !invalidNames.includes(lastName)) invalidNames.push(lastName);
      }
    } catch (err) {
      // skip
    }
  }

  if (!invalidNames.length) return;

  for (const name of invalidNames) {
    const selectors = getSelectorsForControlName(name);
    const handled = scrollToFirstMatchingSelector(root, selectors);
    if (handled) return;
  }
}

export function hasDisplayNoneInHierarchy(el: Element | null): boolean {
  if (!el) return false;

  let current: Element | null = el;

  while (current) {
    try {
      const style = window.getComputedStyle(current);
      if (style && style.display === 'none') return true;
    } catch {
      // ignore
    }

    current = current.parentElement;
  }

  return false;
}
